import React, { useState, useEffect } from "react";
import "./emp-schedule.css";
import { FaUsers } from "react-icons/fa6";
import { FaUserClock } from "react-icons/fa";
import { IoEye } from "react-icons/io5";
import { Col, Dropdown, Button, Form, Modal, Spinner, Badge } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { IoMdAdd } from "react-icons/io";
import { FaRegEdit } from "react-icons/fa";

export default function EmpsSchedule() {
  const navigate = useNavigate();

  const rowsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [empData, setEmpData] = useState([]);
  const [timingData, setTimingData] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Fetch employees
  useEffect(() => {
    const fetchEmployees = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axios.get("https://backend.ems.uk.hiradentallab.com/api/user/getAllUsers");
        const sortedEmployees = response.data.sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        setEmpData(sortedEmployees);
        await fetchTimingData();
      } catch (error) {
        console.error("Error fetching employees:", error);
        setError("Failed to load employee data.");
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, []);

  // Fetch timing data
  const fetchTimingData = async () => {
    try {
      const response = await axios.get("https://backend.ems.uk.hiradentallab.com/api/timings/getAllTimings");
      const timings = response.data.reduce((acc, timing) => {
        acc[timing.userId] = timing;
        return acc;
      }, {});
      setTimingData(timings);
    } catch (error) {
      console.error("Error fetching timing data:", error);
      setError("Failed to load timing data.");
    }
  };

  // Filter data based on search query
  const filteredData = empData
    .filter((employee) =>
      employee.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const totalEntries = filteredData.length;
  const totalPages = Math.ceil(totalEntries / rowsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleViewClick = (employee) => {
    setSelectedEmp(employee);
    setShowModal(true);
  };

  const handleAddClick = (employee) => {
    navigate(`/emps-timing/${employee.userId}`, {
      state: { selectedUserId: employee.userId, shift: timingData[employee.userId]?.shift },
    });
  };

  const handleEditClick = (employee) => {
    navigate(`/edit-timing/${employee.userId}`, {
      state: { selectedUserId: employee.userId, shift: timingData[employee.userId]?.shift },
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedEmp(null);
  };

  // ✅ Updated break summary: based on paidBreakLimitMinutes & unpaidBreakLimitMinutes
  const getBreakStatusSummary = (timingDetails) => {
    if (!timingDetails) return "-";
    const hasPaid = timingDetails.paidBreakLimitMinutes > 0;
    const hasUnpaid = timingDetails.unpaidBreakLimitMinutes > 0;

    if (!hasPaid && !hasUnpaid) return "-";
    if (hasPaid && !hasUnpaid) return "1 Paid";
    if (!hasPaid && hasUnpaid) return "1 Unpaid";
    return "1 Paid, 1 Unpaid";
  };

  return (
    <div className="container-fluid">
      <h1 className="emps-schedule-title">
        <FaUserClock /> Employee Schedule
      </h1>
      <hr />
      <h1 className="emps-schedule-subtitle">
        <FaUsers /> Employees
      </h1>
      <hr />
      {error && <div className="alert alert-danger">{error}</div>}
      
      <div className="emps-schedule-content">
        <div className="filter-container">
          <Col xs={12} md={6} className="filter-left">
            <Dropdown className="d-inline mx-2" autoClose="inside">
              <Dropdown.Toggle id="dropdown-autoclose-inside">
                Show Entries
              </Dropdown.Toggle>
              <Dropdown.Menu className="custom-dropdown-menu">
                <Dropdown.Item href="#">15</Dropdown.Item>
                <Dropdown.Item href="#">25</Dropdown.Item>
                <Dropdown.Item href="#">50</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Col>
          <Col xs={12} md={6} className="filter-right">
            <Form>
              <Form.Control
                type="search"
                placeholder="Search by Name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="depts-search-bar"
              />
            </Form>
          </Col>
        </div>

        {loading ? (
          <div className="text-center my-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-striped">
              <thead className="table-header">
                <tr className="emps-schedule-table-header">
                  <th>SR</th>
                  <th>UserId</th>
                  <th>Employee Name</th>
                  <th>Department</th>
                  <th>Shift</th>
                  <th>Check-In</th>
                  <th>Check-Out</th>
                  <th>Hours</th>
                  <th>Breaks</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((employee, index) => {
                    const timingDetails = timingData[employee.userId] || {};
                    const srNumber = (currentPage - 1) * rowsPerPage + index + 1;
                    
                    return (
                      <tr key={employee._id}>
                        <td>{srNumber}</td>
                        <td>{employee.userId}</td>
                        <td>{employee.name}</td>
                        <td>{employee.department?.name || "N/A"}</td>
                        <td>{timingDetails.shift || "-"}</td>
                        <td>{timingDetails.shiftStart || "-"}</td>
                        <td>{timingDetails.shiftEnd || "-"}</td>
                        <td>{timingDetails.totalHours ? `${timingDetails.totalHours.toFixed(2)} ` : "-"}</td>
                        <td>{getBreakStatusSummary(timingDetails)}</td>
                        <td>
                          <Button variant="success" size="sm" className="me-1" onClick={() => handleAddClick(employee)}>
                            <IoMdAdd />
                          </Button>
                          <Button variant="primary" size="sm" className="me-1" onClick={() => handleViewClick(employee)}>
                            <IoEye />
                          </Button>
                          <Button variant="warning" size="sm" onClick={() => handleEditClick(employee)}>
                            <FaRegEdit />
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="10" className="text-center">No employees found</td>
                  </tr>
                )}
              </tbody>
              <tfoot className="table-footer">
                <tr className="hr-schedule-table-footer">
                  <td>Total</td>
                  <td>-</td>
                  <td>{totalEntries}</td>
                  <td colSpan="7"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="entries-pagination-container">
          <p>
            Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
            {Math.min(currentPage * rowsPerPage, totalEntries)} of {totalEntries} entries
          </p>
          <nav>
            <ul className="pagination">
              <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>
                  Previous
                </button>
              </li>
              {[...Array(totalPages).keys()].map((page) => (
                <li key={page + 1} className={`page-item ${currentPage === page + 1 ? "active" : ""}`}>
                  <button className="page-link" onClick={() => handlePageChange(page + 1)}>
                    {page + 1}
                  </button>
                </li>
              ))}
              <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Modal - now shows break allowances instead of break log */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>📋 Employee Schedule Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEmp && (
            <div>
              <div className="row mb-4">
                <div className="col-md-6">
                  <h5>👤 <strong>{selectedEmp.name}</strong></h5>
                  <p><strong>UserId:</strong> {selectedEmp.userId}</p>
                  <p><strong>Department:</strong> {selectedEmp.department?.name || "N/A"}</p>
                </div>
                <div className="col-md-6">
                  <h5>⏰ Shift Details</h5>
                  <p><strong>Shift:</strong> <Badge bg="info">{timingData[selectedEmp.userId]?.shift || "N/A"}</Badge></p>
                  <p><strong>Check-In:</strong> {timingData[selectedEmp.userId]?.shiftStart || "N/A"}</p>
                  <p><strong>Check-Out:</strong> {timingData[selectedEmp.userId]?.shiftEnd || "N/A"}</p>
                  <p><strong>Total Hours:</strong> <Badge bg="primary">{timingData[selectedEmp.userId]?.totalHours ? `${timingData[selectedEmp.userId].totalHours.toFixed(2)} hrs` : "N/A"}</Badge></p>
                </div>
              </div>

              {/* Break Allowances Section */}
              <div className="mt-4">
                <h5>🍵 Break Allowances</h5>
                {timingData[selectedEmp.userId] ? (
                  <div className="table-responsive">
                    <table className="table table-bordered table-sm">
                      <thead className="table-light">
                        <tr>
                          <th>Type</th>
                          <th>Allowed Minutes</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Paid Break</td>
                          <td>
                            <Badge bg="success">
                              {timingData[selectedEmp.userId].paidBreakLimitMinutes || 0} min
                            </Badge>
                          </td>
                        </tr>
                        <tr>
                          <td>Unpaid Break</td>
                          <td>
                            <Badge bg="warning">
                              {timingData[selectedEmp.userId].unpaidBreakLimitMinutes || 0} min
                            </Badge>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-muted">No timing set</p>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}